import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { PARROT_API_BASE_URL } from '@shared/webapi/MIF.Subscription.Parrot';
import { API_BASE_URL } from '@shared/webapi/MIF.Subscription.WebApi';
import { CustomerService } from '@shared/webapi/services/customer.service';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PublicAuthInterceptor } from '../interceptors/public-auth.interceptor';
import { PublicTokenStorageService } from './public-token-storage.service';
import { MifClientTokenStorageService } from './mif-client-token-storage.service';
import { ResumeAuthService } from './resume-auth.service';

@Injectable()
export class MifSubscriptionTokenInterceptor implements HttpInterceptor {
  private static readonly excludedUrlParts = [
    // todo: add all for public-auth interceptor
    'getAllReference',
    'getReglesMetierProduit',
    'Labels',
    'Offers',
    'Lookups',
    'Settings',
    'parrot_subscription'
  ];

  private static readonly authUrlParts = [
    '/Auth/request-code-prospect',
    '/Auth/request-code-client',
    '/Auth/validate-code',
    '/Auth/resume-session'
  ];

  private isRedirectingToResume = false;

  constructor(
    private readonly injector: Injector,
    @Inject(API_BASE_URL) private readonly apiBaseUrl: string,
    @Inject(PARROT_API_BASE_URL) private readonly parrotApiBaseUrl: string
  ) {}

  // Routes app API calls through either the public token flow or the client token flow.
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isAppApiRequest(request.url)) {
      console.log(`MifSubscriptionTokenInterceptor: Skipping non-app API request to ${request.url}`);
      return next.handle(request);
    }

    if (this.isAuthRequest(request.url)) {
      console.log(`MifSubscriptionTokenInterceptor: Skipping auth request to ${request.url}`);
      return next.handle(this.removeAuthorizationHeader(request));
    }

    if (this.isExcludedRequest(request.url)) {
      console.log(
        `MifSubscriptionTokenInterceptor: Routing excluded request to ${request.url} through PublicAuthInterceptor`
      );
      return this.getPublicAuthInterceptor().intercept(request, next);
    }

    const accessToken = this.getMifClientTokenStorageService().getToken();

    if (!accessToken) {
      console.log(
        `MifSubscriptionTokenInterceptor: No client token for ${request.url}; redirecting to resume flow`
      );
      this.redirectToResumeFlow();
      return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Unauthorized', url: request.url }));
    }

    console.log(`MifSubscriptionTokenInterceptor: Routing request to ${request.url} with bearer token`);
    return next.handle(this.addTokenToRequest(request, accessToken)).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          console.log(`MifSubscriptionTokenInterceptor: Clearing invalid client token after 401 on ${request.url}`);
          this.getMifClientTokenStorageService().clearToken();
          this.redirectToResumeFlow();
        }

        return throwError(() => error);
      })
    );
  }

  // Identifies requests that must keep using the existing public-auth interceptor.
  private isExcludedRequest(url: string): boolean {
    const normalizedUrl = url.toLowerCase();

    return MifSubscriptionTokenInterceptor.excludedUrlParts.some(urlPart =>
      normalizedUrl.includes(urlPart.toLowerCase())
    );
  }

  // Skips token injection for authentication endpoints that issue or validate tokens.
  private isAuthRequest(url: string): boolean {
    const normalizedUrl = url.toLowerCase();

    return MifSubscriptionTokenInterceptor.authUrlParts.some(urlPart => normalizedUrl.includes(urlPart.toLowerCase()));
  }

  // Limits interceptor handling to the subscription WebApi and Parrot API base URLs.
  private isAppApiRequest(url: string): boolean {
    return url.startsWith(this.apiBaseUrl) || url.startsWith(this.parrotApiBaseUrl);
  }

  // Clones the request and adds the validated client bearer token.
  private addTokenToRequest(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    if (!token?.trim()) {
      return request;
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private removeAuthorizationHeader(request: HttpRequest<unknown>): HttpRequest<unknown> {
    if (!request.headers.has('Authorization')) {
      return request;
    }

    return request.clone({
      headers: request.headers.delete('Authorization')
    });
  }

  // Lazily creates the existing public-auth interceptor through DI.
  private getPublicAuthInterceptor(): PublicAuthInterceptor {
    return new PublicAuthInterceptor(this.getPublicTokenStorageService());
  }

  private getPublicTokenStorageService(): PublicTokenStorageService {
    return this.injector.get(PublicTokenStorageService);
  }

  private getMifClientTokenStorageService(): MifClientTokenStorageService {
    return this.injector.get(MifClientTokenStorageService);
  }

  private getResumeAuthService(): ResumeAuthService {
    return this.injector.get(ResumeAuthService);
  }

  private redirectToResumeFlow(): void {
    const currentUrl = this.getRouter().url;

    if (this.isRedirectingToResume || this.getResumeAuthService().isAuthEntryUrl(currentUrl)) {
      return;
    }

    this.isRedirectingToResume = true;

    void this.getResumeAuthService()
      .redirectToResume(this.getCurrentUserId(), currentUrl)
      .finally(() => {
        this.isRedirectingToResume = false;
      });
  }

  private getCurrentUserId(): string | null {
    const currentUrl = this.getRouter().url;
    const userIdFromUrl = this.getRouter().parseUrl(currentUrl).queryParams['userId'];

    if (typeof userIdFromUrl === 'string' && userIdFromUrl.trim()) {
      return userIdFromUrl;
    }

    return this.getCustomerService().userId ?? null;
  }

  private getRouter(): Router {
    return this.injector.get(Router);
  }

  private getCustomerService(): CustomerService {
    return this.injector.get(CustomerService);
  }
}
